import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useRoutes } from 'react-router-dom';
import { resetGenerationWatch } from '@entities/Generation';
import { useAuthStore } from '@entities/User';
import { reportReminderClick } from '@features/EmailUnsubscribe';
import { AnalyticsTracker, logAppOpen, syncAnalyticsUser } from '@shared/analytics';
import { configureAuth } from '@shared/api';
import { captureAttribution } from '@shared/lib/attribution';
import { initializePwaInstall } from '@shared/pwa/install';
import { ErrorBoundary } from './ErrorBoundary';
import { NetworkSync } from './providers/NetworkSync';
import { QueryProvider } from './providers/QueryProvider';
import './i18n/i18n';
import routesConfig from './routes/routesConfig';
import './styles/index.css';

initializePwaInstall();
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => {
      // Installation still works online if offline fallback registration fails.
    });
  });
}

configureAuth({
  getAccessToken: () => useAuthStore.getState().accessToken,
  setAccessToken: (token) => useAuthStore.getState().setAccessToken(token),
  onUnauthorized: () => useAuthStore.getState().signOut(),
});

// Аналитика узнаёт пользователя (user_id в Amplitude и GA4): сохранённого — при старте,
// дальше — при входе, смене аккаунта и выходе.
syncAnalyticsUser(null, useAuthStore.getState().user);
useAuthStore.subscribe((state, prev) => syncAnalyticsUser(prev.user, state.user));
// Заход в приложение — в наш журнал событий для аналитики админки (2026-10-21): сохранённый ученик — при старте,
// дальше — при входе.
logAppOpen(useAuthStore.getState().user?.id);
useAuthStore.subscribe((state) => logAppOpen(state.user?.id));
// Сменился ученик — обнуляем слежение за генерациями (ТЗ главной § 8). Кэш запросов чистит
// qc.clear() при входе, регистрации, выходе и входе через Яндекс; сущности друг друга не
// импортируют, поэтому связь — здесь.
useAuthStore.subscribe((state, prev) => {
  if (state.user?.id !== prev.user?.id) resetGenerationWatch();
});

// До роутера: редирект гостя на /auth уносит метки из адреса. Источник визита нужен только
// будущей регистрации — у вошедшего (стор уже прочитан из localStorage) не запоминаем (B).
if (useAuthStore.getState().stage === 'auth') captureAttribution();
// Клик по ссылке из письма-напоминания засчитывается до входа (A).
reportReminderClick(window.location.search);

function AppRoutes() {
  return useRoutes(routesConfig);
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryProvider>
      <BrowserRouter>
        <AnalyticsTracker />
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
        {/* Уведомления об аналитике в приложении нет (решение владельца 2026-10-15): о cookie и
            аналитике сообщают баннер сайта и согласие при регистрации. */}
        <NetworkSync />
      </BrowserRouter>
    </QueryProvider>
  </StrictMode>,
);
